import React, { useCallback, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Logger from "../../../services/Logger";
import CustomSnackbar from "../../components/CustomSnackbar";
import AdminSlidingPackService from "./AdminSlidingPackService";
import SlidingPackLevelBuilder from "./SlidingPackLevelBuilder";

const LEVEL_MAX = 1200;

const emptyForm = {
    name: "",
    code: "season_",
    levelTo: "1200",
};

function levelLabel(pack) {
    const from = Number(pack?.levelFrom) || 1;
    const to = Number(pack?.levelTo) || LEVEL_MAX;
    return `Levels ${from}–${to}`;
}

export default function AdminSlidingPackPage() {
    const [packs, setPacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [busyCode, setBusyCode] = useState("");
    const [form, setForm] = useState(emptyForm);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

    const notify = (severity, message) => setSnack({ open: true, severity, message });

    const loadPacks = useCallback(async () => {
        setLoading(true);
        setLoadError("");
        try {
            const items = await AdminSlidingPackService.list();
            setPacks(Array.isArray(items) ? items : []);
        } catch (error) {
            Logger.error("Sliding pack list failed:", error);
            setPacks([]);
            setLoadError(error?.message || "Could not load sliding packs");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPacks();
    }, [loadPacks]);

    const patchPack = (code, patch) => {
        setPacks((current) => current.map((pack) => (
            pack.code === code ? { ...pack, ...patch } : pack
        )));
    };

    const handleCreate = async () => {
        const levelTo = Number(form.levelTo);
        if (!form.name.trim()) {
            notify("error", "Pack name is required");
            return;
        }
        if (!String(form.code).trim().toLowerCase().startsWith("season_")) {
            notify("error", "Season pack codes start with season_");
            return;
        }
        if (!Number.isInteger(levelTo) || levelTo < 1 || levelTo > LEVEL_MAX) {
            notify("error", `Level must be a whole number from 1 to ${LEVEL_MAX}`);
            return;
        }
        setBusyCode("create");
        try {
            await AdminSlidingPackService.create({
                name: form.name.trim(),
                code: form.code.trim(),
                levelTo,
            });
            setForm(emptyForm);
            notify("success", "Pack created");
            await loadPacks();
        } catch (error) {
            Logger.error("Sliding pack create failed:", error);
            notify("error", error?.message || "Could not create pack");
        } finally {
            setBusyCode("");
        }
    };

    const handleSave = async (pack) => {
        const levelTo = Number(pack.levelTo);
        if (!String(pack.name || "").trim()) {
            notify("error", "Pack name is required");
            return;
        }
        if (!Number.isInteger(levelTo) || levelTo < 1 || levelTo > LEVEL_MAX) {
            notify("error", `Level must be a whole number from 1 to ${LEVEL_MAX}`);
            return;
        }
        setBusyCode(pack.code);
        try {
            await AdminSlidingPackService.update(pack.code, {
                name: String(pack.name).trim(),
                levelTo,
            });
            notify("success", "saved");
            await loadPacks();
        } catch (error) {
            Logger.error("Sliding pack update failed:", error);
            notify("error", error?.message || "Could not save pack");
            await loadPacks();
        } finally {
            setBusyCode("");
        }
    };

    const handleDelete = async () => {
        const pack = pendingDelete;
        if (!pack?.code) {
            return;
        }
        setBusyCode(pack.code);
        setPendingDelete(null);
        try {
            await AdminSlidingPackService.remove(pack.code);
            notify("success", "deleted");
            await loadPacks();
        } catch (error) {
            Logger.error("Sliding pack delete failed:", error);
            notify("error", error?.message || "Could not remove pack");
        } finally {
            setBusyCode("");
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: "primary.main", mb: 1 }}>
                Sliding Packs
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
                Season packs only. Pick published images, then fill levels 1–{LEVEL_MAX} from a 4×4 lower scramble up to a 6×6 high scramble.
            </Alert>

            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Typography sx={{ mb: 1, fontWeight: 600 }}>New season pack</Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                    <TextField
                        size="small"
                        label="Name"
                        value={form.name}
                        onChange={(event) => setForm({ ...form, name: event.target.value })}
                    />
                    <TextField
                        size="small"
                        label="Code"
                        value={form.code}
                        helperText="Starts with season_"
                        onChange={(event) => setForm({ ...form, code: event.target.value })}
                    />
                    <TextField
                        size="small"
                        label="Through level"
                        type="number"
                        value={form.levelTo}
                        onChange={(event) => setForm({ ...form, levelTo: event.target.value })}
                        slotProps={{ htmlInput: { min: 1, max: LEVEL_MAX, step: 1 } }}
                        sx={{ width: 140 }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleCreate}
                        disabled={busyCode === "create"}
                        sx={{ minHeight: 44 }}
                    >
                        Create pack
                    </Button>
                </Box>
            </Paper>

            {loading ? <CircularProgress /> : null}
            {!loading && loadError ? <Alert severity="error">{loadError}</Alert> : null}
            {!loading && !loadError && packs.length === 0 ? (
                <Alert severity="info">No season pack is set up yet.</Alert>
            ) : null}

            {!loading && packs.map((pack) => {
                const busy = busyCode === pack.code;
                return (
                    <Paper key={pack.code} elevation={1} sx={{ p: 2, mb: 2 }}>
                        <Typography sx={{ fontWeight: 600, mb: 1 }}>
                            {pack.name} ({pack.code}) — {levelLabel(pack)}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                            <TextField
                                size="small"
                                label="Name"
                                value={pack.name || ""}
                                disabled={busy}
                                onChange={(event) => patchPack(pack.code, { name: event.target.value })}
                            />
                            <TextField
                                size="small"
                                label="Code"
                                value={pack.code}
                                disabled
                            />
                            <TextField
                                size="small"
                                label="Through level"
                                type="number"
                                value={pack.levelTo}
                                disabled={busy}
                                onChange={(event) => patchPack(pack.code, { levelTo: event.target.value })}
                                slotProps={{ htmlInput: { min: 1, max: LEVEL_MAX, step: 1 } }}
                                sx={{ width: 140 }}
                            />
                            <Button
                                variant="contained"
                                onClick={() => handleSave(pack)}
                                disabled={busy}
                                sx={{ minHeight: 44 }}
                            >
                                Save
                            </Button>
                            <Button
                                color="error"
                                onClick={() => setPendingDelete(pack)}
                                disabled={busy}
                                sx={{ minHeight: 44 }}
                            >
                                Remove
                            </Button>
                        </Box>
                        <SlidingPackLevelBuilder
                            packCode={pack.code}
                            levelTo={Number(pack.levelTo) || LEVEL_MAX}
                            onNotify={notify}
                        />
                    </Paper>
                );
            })}

            <Dialog open={Boolean(pendingDelete)} onClose={() => setPendingDelete(null)}>
                <DialogTitle>Remove season pack</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Remove {pendingDelete?.name} ({pendingDelete?.code})? This only deletes the pack list entry.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingDelete(null)} sx={{ minHeight: 44 }}>
                        Cancel
                    </Button>
                    <Button color="error" onClick={handleDelete} sx={{ minHeight: 44 }}>
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>

            <CustomSnackbar
                open={snack.open}
                setOpen={(open) => setSnack((current) => ({ ...current, open }))}
                severity={snack.severity}
                message={snack.message}
            />
        </Box>
    );
}
